import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import { NOTE_MAX_LENGTH } from '../../../../shared/demand-notes.js';
import { canReturnDemand } from '../../../../shared/demand-transitions.js';

/**
 * Needs Clarification for one line (HS2-R5H22P): the reviewer's question goes to the line's notes,
 * the line is flagged and made undecided, and the demand goes back to its requester while staff keep
 * working. Without a requester (or once already returned) it only flags the line and posts the note.
 */
export function ClarifyDialog({
  lineNumber,
  lineDescription,
  demandStatus,
  hasRequester,
}: {
  lineNumber: number;
  lineDescription: string;
  demandStatus: string;
  hasRequester: boolean;
}): SafeHtml {
  // Undeciding the line takes a decided demand back to a returnable status first.
  const returns = hasRequester && (demandStatus === 'decided' || canReturnDemand(demandStatus, hasRequester));
  return (
    <DialogSurface
      label={`Needs Clarification · Line ${String(lineNumber)}`}
      lightDismiss
      className="clarify-dialog"
      rootAttributes={{ 'data-unsaved-guard': 'this question', 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action="clarify-cancel"
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action="clarify-send"
            rootAttributes={{ 'data-unsaved-save': '' }}
          >
            {returns ? 'Send to Requester' : 'Flag Line'}
          </Button>
        </>
      }
    >
      <List gap="m">
        <Text variant="p">
          {returns
            ? `Ask the requester about “${lineDescription}”. Your question goes to the line's notes, the line becomes undecided, and the demand returns to the requester. You can keep working on it meanwhile.`
            : `Your question goes to the notes of “${lineDescription}”, and the line is flagged and becomes undecided.`}
        </Text>
        <TextField
          multiline
          name="clarify-question"
          label="Question for the Requester"
          rows={3}
          maxLength={NOTE_MAX_LENGTH}
          autofocus
        />
      </List>
    </DialogSurface>
  );
}
